'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, memo } from 'react';
import { Row, TransformedNewsItem, Section, Column } from '@/lib/types/pageBuilder';
import ColumnRenderer from './ColumnRenderer';
import SectionHeading from './SectionHeading';
import { DynamicTabInfo } from './TabBarSection';
import { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';

// Tailwind class maps shared with ColumnRenderer for skeleton width sizing
const fbColSpan: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12'
};
const fbMdColSpan: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12'
};
const fbLgColSpan: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12'
};

/**
 * Skeleton fallback that occupies the same grid cell the streamed column
 * will land in, with a reserved min-height proportional to the expected
 * content. Prevents the page from painting empty regions and then jumping
 * when the Suspense boundary resolves.
 */
function ColumnSkeletonFallback(t0) {
  const $ = _c(5);
  const {
    column,
    itemCount
  } = t0;
  const widthClass = `${fbColSpan[column.width?.mobile || 12] || "col-span-12"} ${fbMdColSpan[column.width?.tablet || 12] || "md:col-span-12"} ${fbLgColSpan[column.width?.desktop || 12] || "lg:col-span-12"}`;
  let reservedHeight = 0;
  if (itemCount > 0) {
    reservedHeight = Math.min(2400, Math.max(250, itemCount * 200));
  } else {
    if (column.includeSection) {
      reservedHeight = 500;
    } else {
      if (column.tabBar || column.slider) {
        reservedHeight = 400;
      }
    }
  }
  const t1 = `${widthClass} flex flex-col`;
  let t2;
  if ($[0] !== reservedHeight) {
    t2 = reservedHeight > 0 ? {
      minHeight: reservedHeight
    } : undefined;
    $[0] = reservedHeight;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className={t1} style={t2} aria-hidden="true" />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
interface RowRendererProps {
  row: Row;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabsMap?: Map<string, DynamicTabInfo[]>;
  includeSectionsData?: Map<string, Section>;
  includeSectionApiData?: IncludeSectionApiData;
  isLastRow?: boolean;
  parentColumnData?: TransformedNewsItem[]; // Data inherited from parent column's data_binding
  dataStartIndex?: number; // Start index for sequential data consumption
  /** Is this the first row on the page (for LCP priority) */
  isFirstRow?: boolean;
}

/**
 * Calculate how many items a component will consume
 */
function getComponentItemCount(component: {
  data_config?: {
    mode?: string;
    loop_count?: number;
  };
}): number {
  if (!component.data_config) return 1;
  if (component.data_config.mode === 'loop') {
    return component.data_config.loop_count || 1;
  }
  return 1;
}

/**
 * Calculate total items consumed by a column and its nested elements
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 *
 * `includeSectionsData` lets an `includeSection` column (which embeds an
 * entire separate page-builder Section by slug, not a components/rows tree
 * of its own) count the *real* nested content instead of falling back to a
 * blind guess — the resolved Section is already fetched server-side before
 * this runs, so the lookup is synchronous.
 */
function getColumnItemCount(column: Column, includeSectionsData?: Map<string, Section>): number {
  let count = 0;
  if (column.components) {
    for (const comp of column.components) {
      count += getComponentItemCount(comp);
    }
  }
  if (column.columns) {
    for (const col of column.columns) {
      count += getColumnItemCount(col, includeSectionsData);
    }
  }
  if (column.rows) {
    for (const row of column.rows) {
      count += getRowItemCount(row, includeSectionsData);
    }
  }
  if (column.includeSection) {
    const nestedSection = includeSectionsData?.get(column.includeSection.slug);
    if (nestedSection) {
      for (const row of nestedSection.rows) {
        count += getRowItemCount(row, includeSectionsData);
      }
    }
  }
  return count;
}

/**
 * Calculate total items consumed by a row's columns
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 */
function getRowItemCount(row: {
  columns?: Column[];
}, includeSectionsData?: Map<string, Section>): number {
  let count = 0;
  if (row.columns) {
    for (const col of row.columns) {
      count += getColumnItemCount(col, includeSectionsData);
    }
  }
  return count;
}
function RowRenderer({
  row,
  collectionsData,
  dynamicTabsMap,
  includeSectionsData,
  includeSectionApiData,
  isLastRow,
  parentColumnData,
  dataStartIndex = 0,
  isFirstRow = false
}: RowRendererProps) {
  // Skip hidden rows
  if (row.hidden) {
    return null;
  }

  // Get items for this row's data binding
  // Priority: 1. This row's own data_binding (resets index), 2. Parent column's data (continues index)
  const rawRowDataBindingKey = row.data_binding?.api_endpoint || row.data_binding?.collection_id;
  // Normalize the endpoint to match how data is stored in collectionsData
  const rowDataBindingKey = rawRowDataBindingKey ? normalizeApiEndpoint(rawRowDataBindingKey) : undefined;
  const hasOwnDataBinding = !!rowDataBindingKey;
  const rawItems = rowDataBindingKey ? collectionsData.get(rowDataBindingKey) || [] : parentColumnData || [];

  // Filter out metadata items BEFORE slicing (metadata items have _isCollectionMeta flag)
  const rowItems = rawItems.filter(item => !item._isCollectionMeta);
  if (rowDataBindingKey && rowItems.length === 0) {
    console.error(`[RowRenderer] WARNING: Row ${row.id} has data_binding "${rowDataBindingKey}" but 0 items! Available keys: ${Array.from(collectionsData.keys()).join(', ')}`);
  }

  // DEBUG logging removed - was causing issues in Firefox/Safari

  // If row has its own data_binding, start from 0; otherwise continue from parent's index
  const startOffset = hasOwnDataBinding ? 0 : dataStartIndex;

  // Render columns with sequential data offsets
  let runningOffset = startOffset;

  // Check if any column has sticky — stretch containers so sticky has room to work
  const hasStickyColumn = row.columns.some(col => col.sticky);
  return <div className={hasStickyColumn ? 'flex-1 flex flex-col' : ''}>
      {/* Row heading */}
      {row.heading?.enabled && <SectionHeading heading={row.heading} />}

      <div className={`grid grid-cols-12${hasStickyColumn ? ' flex-1' : ''}`}>
        {row.columns.map((column, index) => {
        const itemCount = getColumnItemCount(column, includeSectionsData);
        // Limit priority to the FIRST column of the first row only.
        // Each next/image with priority emits a separate <link rel="preload">,
        // and SEO audits flag >2 image preloads as bandwidth-wasteful.
        const columnPriority = isFirstRow && index === 0;
        const element = <Suspense key={column.id} fallback={<ColumnSkeletonFallback column={column} itemCount={itemCount} />}>
              <ColumnRenderer column={column} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={index === row.columns.length - 1} parentColumnData={rowItems} dataStartIndex={runningOffset} priority={columnPriority} />
            </Suspense>;
        runningOffset += itemCount;
        return element;
      })}
      </div>
    </div>;
}

// Memoized: below-fold rows share a mutable collectionsData/dynamicTabsMap
// reference that grows in place as new batches load (see BelowFoldContentLazy).
// Without this, every new batch re-renders every already-rendered row too,
// which shows up as already-visible cards flickering while unrelated content
// is still loading in.
export default memo(RowRenderer);